import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookOpenCheck, ChevronDown, Mic, Trash2, Video } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TimeSelect } from "@/components/TimeSelect";
import { MatterCombobox } from "@/components/MatterCombobox";
import { ColorField } from "@/components/ColorField";
import { MarkdownField } from "@/components/MarkdownField";
import { Markdown } from "@/components/Markdown";
import { FeeNoteButton } from "@/components/FeeNoteButton";
import { api, errText } from "@/lib/api";
import { FORUM_PALETTE, courtColor, readableOn } from "@/lib/forumColors";
import {
  EVENT_TYPES,
  POST_COURT_KINDS,
  SESSION_FLAGS,
  TIMED_TYPES,
  fmtTime,
  isoDate,
  longDate,
  plusHour,
  sessionEndFor,
} from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

const blank = (date) => ({
  date: date || isoDate(new Date()),
  event_type: "session_based",
  title: "",
  matter_id: null,
  court_id: null,
  start_time: null,
  end_time: null,
  session_flag: "both",
  location: "",
  post_court_kind: "arbitration",
  urgent: false,
  notes: "",
  listing_details: "",
  color: null,
  vc_link: "",
  vc_platform: null,
});

const matterLabel = (matter) =>
  matter ? [matter.title, matter.case_number].filter(Boolean).join(" — ") : "";

export default function EventDialog({
  open,
  onOpenChange,
  event,
  defaultDate,
  onSaved,
  onVoiceLog,
}) {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(blank(defaultDate));
  const [courts, setCourts] = useState([]);
  const [matters, setMatters] = useState([]);
  const [saving, setSaving] = useState(false);
  const [clashes, setClashes] = useState([]);
  const [current, setCurrent] = useState(null);
  const [showClash, setShowClash] = useState(false);
  const autoTitle = useRef("");
  const autoCourt = useRef(null);
  const manualEnd = useRef(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open || form.event_type !== "fixed_time" || !form.start_time || !form.date) {
      setClashes([]);
      return;
    }
    const [h, m] = form.start_time.split(":").map(Number);
    const mine = h * 60 + m;
    api
      .get("/events", { params: { start: form.date, end: form.date } })
      .then(({ data }) => {
        setClashes(
          data.filter((e) => {
            if (e.event_type !== "fixed_time" || !e.start_time) return false;
            if (current?.event_id && e.event_id === current.event_id) return false;
            const [eh, em] = e.start_time.split(":").map(Number);
            return Math.abs(eh * 60 + em - mine) <= 30;
          }),
        );
      })
      .catch(() => setClashes([]));
  }, [open, form.event_type, form.start_time, form.date, current?.event_id]);

  useEffect(() => {
    if (!open) return;
    setCurrent(event || null);
    setShowClash(false);
    autoTitle.current = "";
    autoCourt.current = null;
    manualEnd.current = Boolean(event?.end_time);
    setForm(event ? { ...blank(defaultDate), ...event } : blank(defaultDate));
    Promise.all([api.get("/courts"), api.get("/matters")])
      .then(([c, m]) => {
        setCourts(c.data);
        setMatters(m.data);
      })
      .catch((e) => toast.error(errText(e)));
  }, [open, event, defaultDate]);

  // Picking a matter names the entry and brings its court across, unless you set your own.
  const pickMatter = (id) => {
    const matter = matters.find((m) => m.matter_id === id);
    const label = matterLabel(matter);
    setForm((f) => {
      const typed = f.title.trim() && f.title !== autoTitle.current;
      const chosenCourt = f.court_id && f.court_id !== autoCourt.current;
      const court_id = chosenCourt ? f.court_id : matter?.court_id || f.court_id || null;
      const court = courts.find((c) => c.court_id === court_id);
      return {
        ...f,
        matter_id: id,
        title: typed ? f.title : label,
        court_id,
        end_time:
          manualEnd.current || f.event_type !== "fixed_time"
            ? f.end_time
            : sessionEndFor(f.start_time, court),
      };
    });
    autoTitle.current = label;
    autoCourt.current = matter?.court_id || null;
  };

  // A fixed-time listing runs to the end of the session it starts in; the rest run an hour.
  const pickStart = (value) => {
    const court = courts.find((c) => c.court_id === form.court_id);
    setForm((f) => ({
      ...f,
      start_time: value,
      end_time: manualEnd.current
        ? f.end_time
        : f.event_type === "fixed_time"
          ? sessionEndFor(value, court)
          : plusHour(value),
    }));
  };

  const pickCourt = (courtId) => {
    const court = courts.find((c) => c.court_id === courtId);
    setForm((f) => ({
      ...f,
      court_id: courtId,
      end_time:
        manualEnd.current || f.event_type !== "fixed_time"
          ? f.end_time
          : sessionEndFor(f.start_time, court),
    }));
  };

  const save = useCallback(async () => {
    if (!form.title.trim()) return toast.error("Give the entry a title");
    if (form.event_type === "fixed_time" && !form.start_time)
      return toast.error("Pick a time slot for a fixed-time listing");
    if (form.event_type === "non_court" && !form.start_time)
      return toast.error("Pick a start time for the non-court hearing");
    setSaving(true);
    const timed = TIMED_TYPES.includes(form.event_type);
    const payload = {
      date: form.date,
      event_type: form.event_type,
      title: form.title.trim(),
      matter_id: form.matter_id || null,
      court_id: form.court_id || null,
      start_time: timed ? form.start_time || null : null,
      end_time: timed ? form.end_time || null : null,
      session_flag: form.event_type === "session_based" ? form.session_flag : null,
      location: form.event_type === "out_of_station" ? form.location : null,
      post_court_kind: form.event_type === "post_court" ? form.post_court_kind : null,
      urgent: !!form.urgent,
      notes: form.notes || null,
      listing_details: (form.listing_details || "").trim() || null,
      is_placeholder: !form.matter_id,
      color: form.court_id ? null : form.color || null,
      vc_link: (form.vc_link || "").trim() || null,
      vc_platform: form.vc_platform || null,
    };
    try {
      if (current?.event_id) await api.put(`/events/${current.event_id}`, payload);
      else await api.post("/events", payload);
      toast.success(current?.event_id ? "Entry updated" : "Entry added to diary");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  }, [form, current, onOpenChange, onSaved]);

  const remove = async () => {
    try {
      await api.delete(`/events/${current.event_id}`);
      toast.success("Entry removed");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl rounded-sm max-h-[75vh] overflow-y-auto p-5 sm:p-6 gap-3"
        data-testid="event-dialog"
      >
        <DialogHeader className="space-y-1">
          <DialogTitle className="font-heading text-xl">
            {current?.event_id ? "Edit diary entry" : "New diary entry"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Overlapping listings are always allowed — nothing here blocks another entry.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
            {EVENT_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                data-testid={`event-type-${t.value}`}
                onClick={() => set({ event_type: t.value })}
                className={`px-2 py-2 rounded-sm border text-xs font-semibold transition-colors duration-200 ${
                  form.event_type === t.value
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-accent"
                }`}
              >
                {t.short}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-caps">Date</Label>
              <Input
                type="date"
                data-testid="event-date-input"
                value={form.date}
                onChange={(e) => e.target.value && set({ date: e.target.value })}
                onClick={(e) => {
                  try {
                    e.currentTarget.showPicker?.();
                  } catch {
                    /* browsers that refuse showPicker still open on their own */
                  }
                }}
                className="rounded-sm h-10 text-sm"
              />
              <p className="text-xs text-muted-foreground">{longDate(form.date)}</p>
            </div>

            <div className="space-y-1.5">
              <Label className="label-caps">Court</Label>
              <Select
                value={form.court_id || "none"}
                onValueChange={(v) => pickCourt(v === "none" ? null : v)}
              >
                <SelectTrigger data-testid="event-court-select" className="rounded-sm h-10 text-sm">
                  <SelectValue placeholder="Select court" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No court</SelectItem>
                  {courts.map((c) => (
                    <SelectItem key={c.court_id} value={c.court_id}>
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: courtColor(c, courts.indexOf(c)) }}
                        />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-caps">Linked matter</Label>
              <MatterCombobox
                matters={matters}
                value={form.matter_id}
                onChange={pickMatter}
                testId="event-matter-select"
                emptyLabel="Unlinked placeholder — assign later"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-caps">Title</Label>
              <Input
                data-testid="event-title-input"
                value={form.title}
                onChange={(e) => set({ title: e.target.value })}
                placeholder="e.g. Sharma v. State — arguments on bail"
                className="rounded-sm h-10 text-sm"
              />
            </div>
          </div>

          {form.event_type === "session_based" ? (
            <div className="space-y-1.5">
              <Label className="label-caps">Session block</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {SESSION_FLAGS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    data-testid={`session-flag-${s.value}`}
                    onClick={() => set({ session_flag: s.value })}
                    className={`px-2 py-2 rounded-sm border text-xs font-medium transition-colors duration-200 ${
                      form.session_flag === s.value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:bg-accent"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {TIMED_TYPES.includes(form.event_type) ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="label-caps">
                  {form.event_type === "morning" ? "Starts at (optional)" : "Starts at"}
                </Label>
                <TimeSelect
                  value={form.start_time}
                  onChange={pickStart}
                  testId="event-time-select"
                  className="rounded-sm h-10 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-caps">
                  {form.event_type === "fixed_time"
                    ? `Ends at · ${manualEnd.current ? "your own time" : "session end"}`
                    : "Ends at"}
                </Label>
                <TimeSelect
                  value={form.end_time}
                  onChange={(v) => {
                    manualEnd.current = true;
                    set({ end_time: v });
                  }}
                  testId="event-end-time-select"
                  placeholder={
                    form.event_type === "fixed_time" ? "End of the session" : "An hour later"
                  }
                  className="rounded-sm h-10 text-sm"
                />
              </div>
              {form.event_type === "post_court" ? (
                <div className="space-y-1.5">
                  <Label className="label-caps">Kind</Label>
                  <Select
                    value={form.post_court_kind || "arbitration"}
                    onValueChange={(v) => set({ post_court_kind: v })}
                  >
                    <SelectTrigger
                      data-testid="post-court-kind-select"
                      className="rounded-sm h-10 text-sm"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POST_COURT_KINDS.map((k) => (
                        <SelectItem key={k.value} value={k.value}>
                          {k.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              {form.event_type === "morning" ? (
                <p className="sm:col-span-3 text-xs text-muted-foreground">
                  Sits before the first court listing of the day — leave the times blank if it is
                  simply "first thing".
                </p>
              ) : null}
              {form.event_type === "non_court" ? (
                <p className="sm:col-span-3 text-xs text-muted-foreground">
                  A hearing away from court — arbitration, tribunal or conference. It can sit at any
                  hour of the day.
                </p>
              ) : null}
            </div>
          ) : null}

          {form.event_type === "all_day" ? (
            <p
              data-testid="all-day-hint"
              className="text-xs text-muted-foreground border border-dashed border-border rounded-sm px-3 py-2"
            >
              An all-day matter blocks the whole day visually — no time or session needed.
            </p>
          ) : null}

          {form.event_type === "out_of_station" ? (
            <div className="space-y-1.5">
              <Label className="label-caps">Station / city</Label>
              <Input
                data-testid="event-location-input"
                value={form.location || ""}
                onChange={(e) => set({ location: e.target.value })}
                placeholder="e.g. Lucknow Bench"
                className="rounded-sm h-10 text-sm"
              />
            </div>
          ) : null}

          {form.court_id ? null : (
            <div className="space-y-1.5">
              <Label className="label-caps">Colour for this entry</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="event-color-swatches">
                <button
                  type="button"
                  data-testid="event-color-default"
                  onClick={() => set({ color: null })}
                  className={`h-7 px-2.5 rounded-sm border text-xs font-semibold transition-colors duration-200 ${
                    form.color
                      ? "border-border hover:bg-accent"
                      : "bg-primary text-primary-foreground border-primary"
                  }`}
                >
                  Default
                </button>
                {FORUM_PALETTE.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    data-testid={`event-color-${hex.replace("#", "")}`}
                    onClick={() => set({ color: hex })}
                    className="h-7 w-7 rounded-sm border border-border grid place-items-center transition-transform duration-200 hover:scale-110"
                    style={{ background: hex }}
                  >
                    {form.color?.toUpperCase() === hex ? (
                      <span className="text-xs font-bold" style={{ color: readableOn(hex) }}>
                        ✓
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
              <ColorField
                value={form.color || ""}
                testId="event-color-custom"
                onChange={(hex) => set({ color: hex })}
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-caps">Video conference link</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="event-vc-link"
                  value={form.vc_link || ""}
                  onChange={(e) => set({ vc_link: e.target.value })}
                  placeholder="https://us02web.zoom.us/j/…"
                  className="rounded-sm h-10 text-sm"
                />
                {form.vc_link ? (
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="event-vc-join"
                    onClick={() => window.open(form.vc_link, "_blank", "noopener")}
                    className="rounded-sm h-10 px-3 shrink-0 text-sm"
                  >
                    <Video className="h-4 w-4 mr-1.5" /> Join
                  </Button>
                ) : null}
              </div>
              {form.vc_platform ? (
                <p className="text-xs text-muted-foreground">{form.vc_platform}</p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-caps">Listing details</Label>
              <MarkdownField
                value={form.listing_details || ""}
                onChange={(v) => set({ listing_details: v })}
                testId="event-listing-details-input"
                minHeight={72}
                placeholder={"Item 24 · Board B\nFor directions"}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-caps">Notes</Label>
              <MarkdownField
                value={form.notes || ""}
                onChange={(v) => set({ notes: v })}
                testId="event-notes-input"
                minHeight={72}
                placeholder="Counsel, instructions, anything private"
              />
            </div>
          </div>

          {clashes.length ? (
            <div
              data-testid="clash-warning"
              className="border rounded-sm px-3 py-2.5"
              style={{
                borderColor: "rgb(var(--c-fixed) / 0.45)",
                background: "rgb(var(--c-fixed) / 0.08)",
              }}
            >
              <p className="label-caps">Heads up — not a blocker</p>
              <p className="text-sm font-semibold mt-0.5">
                {clashes.length} other fixed slot{clashes.length > 1 ? "s" : ""} within 30 minutes
              </p>
              <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                {clashes.slice(0, 3).map((c) => (
                  <li key={c.event_id}>
                    {fmtTime(c.start_time)} · {c.title}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                data-testid="clash-more-info-btn"
                onClick={() => setShowClash((v) => !v)}
                className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold underline"
              >
                {showClash ? "Hide details" : "More info"}
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${
                    showClash ? "rotate-180" : ""
                  }`}
                />
              </button>
              {showClash ? (
                <div className="mt-2 space-y-2" data-testid="clash-details">
                  {clashes.map((c) => (
                    <div
                      key={c.event_id}
                      data-testid={`clash-detail-${c.event_id}`}
                      className="border border-border rounded-sm bg-background px-3 py-2"
                      style={{
                        borderLeft: `5px solid ${
                          c.court_color || c.color || "rgb(var(--c-fixed))"
                        }`,
                      }}
                    >
                      <p className="text-xs font-bold">{fmtTime(c.start_time)}</p>
                      <p className="text-sm font-semibold leading-snug">{c.title}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {[c.matter_title || "No matter linked", c.case_number, c.court_name]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <Markdown text={c.notes} className="mt-1 text-xs text-muted-foreground" />
                      <button
                        type="button"
                        data-testid={`clash-open-${c.event_id}`}
                        onClick={() => {
                          setCurrent(c);
                          setForm({ ...blank(defaultDate), ...c });
                          setShowClash(false);
                        }}
                        className="mt-1.5 text-xs font-semibold underline"
                      >
                        Open this entry
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
            <div>
              <p className="text-sm font-semibold">Mark urgent</p>
              <p className="text-xs text-muted-foreground">Highlights this listing in red</p>
            </div>
            <Switch
              data-testid="event-urgent-switch"
              checked={!!form.urgent}
              onCheckedChange={(v) => set({ urgent: v })}
            />
          </div>
        </div>

        <DialogFooter className="flex-row flex-wrap gap-2 sm:justify-end">
          {current?.event_id ? (
            <Button
              variant="outline"
              onClick={remove}
              data-testid="event-delete-btn"
              className="rounded-full h-9 px-3 text-sm mr-auto text-destructive border-destructive/40 hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
            </Button>
          ) : null}
          {current?.event_id ? (
            <Button
              variant="outline"
              data-testid="event-kit-btn"
              onClick={() => {
                onOpenChange(false);
                navigate(`/kit?event=${current.event_id}`);
              }}
              className="rounded-full h-9 px-3 text-sm"
            >
              <BookOpenCheck className="h-4 w-4 mr-1.5" /> Day kit
            </Button>
          ) : null}
          {current?.event_id && onVoiceLog ? (
            <Button
              variant="outline"
              data-testid="event-voice-log-btn"
              onClick={() => {
                onOpenChange(false);
                onVoiceLog({ ...current, ...form });
              }}
              className="rounded-full h-9 px-3 text-sm"
            >
              <Mic className="h-4 w-4 mr-1.5" /> Voice log
            </Button>
          ) : null}
          {current?.event_id && form.matter_id ? (
            <FeeNoteButton
              matterId={form.matter_id}
              eventId={current.event_id}
              className="rounded-full h-9 px-3 text-sm"
              testId="event-fee-note-btn"
            />
          ) : null}
          <Button
            variant="outline"
            className="rounded-full h-9 px-4 text-sm"
            onClick={() => onOpenChange(false)}
            data-testid="event-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            className="rounded-full h-9 px-5 text-sm"
            onClick={save}
            disabled={saving || !canWrite}
            data-testid="event-save-btn"
          >
            {saving ? "Saving…" : current?.event_id ? "Save changes" : "Add to diary"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
